import * as Sentry from "@sentry/react";
import cx from "classnames";
import React, { useContext, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import useErrorLogger from "../../common/hooks/useErrorLogger";
import { getCurrentQuestion, getPreviousQuestion, setCurrentPageProgress, skipCurrentQuestion, submitAnswer } from "../api/ApiAppForm";
import AFCannotContinue from "../components/AFCannotContinue";
import AFLayout from "../components/AFLayout";
import AFMultiForms from "../components/AFMultiForms";
import AppQuestionForm from "../components/AppQuestionForm";
import AFQuestionPlaceholder from "../components/placeholders/AFQuestionPlaceholder";
import { PageType } from "../enums/PageType";
import { IAFQuestionData, IAppFormQuestion, IPageInfo } from "../interfaces/IAppFormData";
import { AppFormControllerContext } from "../providers/AppFormProvider";
import styles from "../scss/AppForm.module.scss";
import {
    getQuestionID,
    getQuestionLabel,
    getQuestionType,
    redirectTo404Page
} from "../shared/AppFormHelper";


type AppFormParams = {
    applicantToken: string;
};

const AppForm: React.FC = () => {
    const history = useHistory();
    const [isFormReady, setIsFormReady] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [currentQuestion, setCurrentQuestion] = useState<IAppFormQuestion>();
    const [currentQuestionList, setCurrentQuestionList] = useState<IAppFormQuestion[]>();
    const params = useParams<AppFormParams>();
    const applicantToken = params.applicantToken;
    const [isMultipleQuestion, setIsMultipleQuestion] = useState(false);
    const [noAvailableQuestion, setNoAvailableQuestion] = useState(false);
    const [formQuestionLabel, setFormQuestionLabel] = useState("");
    const [formQuestionHelpText, setFormQuestionHelpText] = useState("");
    const [formQuestionType, setFormQuestionType] = useState("");
    const [isInErrorState, setIsInErrorState] = useState(false);
    const [isHalted, setIsHalted] = useState(false);
    const [haltedReasons, setHaltedReasons] = useState<string[]>([]);

    const { actions, selectors } = useContext(AppFormControllerContext);

    const handleChangeQuestion = (response: IAppFormQuestion[]): void => {
        setCurrentQuestionList([]);
        setFormQuestionLabel("");
        setIsMultipleQuestion(response?.length > 1);
        setNoAvailableQuestion(false);
        setIsFormReady(true);
        if (response?.length > 1) {
            const firstQuestion = response[0];
            setCurrentQuestionList(response);
            setFormQuestionLabel(getQuestionLabel(firstQuestion));
            setFormQuestionHelpText(firstQuestion?.questionHeaderHelp)
            setFormQuestionType(getQuestionType(firstQuestion));
            return;
        } else if (response?.length) {
            setCurrentQuestion(response[0]);
            return;
        }
        setNoAvailableQuestion(true);
    };

    const handleGetQuestion = (applicantToken): void => {
        setIsFormReady(false);
        setCurrentQuestionList([]);
        getCurrentQuestion(applicantToken)
            .then((res) => {
                setIsFormReady(true);
                if (res?.halt) {
                    setIsHalted(true);
                    setHaltedReasons(res?.haltReasons);
                    return;
                }
                if (!res?.questions?.length) {
                    setIsInErrorState(true);
                    return;
                }
                handleChangeQuestion(res.questions);
                actions.setQuestionResponse(res.response);
            })
            .catch((res) => {
                useErrorLogger();
                setIsInErrorState(true);
            });
    };

    const handleNextQuestion = (params): void => {
        const paramObj = {
            answer: {
                answers: params
            },
            response: selectors.getQuestionResponse()
        };
        setIsSubmitting(true);
        setIsFormReady(false);
        actions.setQuestionResponse(null);
        submitAnswer(applicantToken, paramObj)
            .then((res) => {
                handleNextPage(res);
            })
            .catch(() => {
                useErrorLogger();
                setIsInErrorState(true);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const handleBack = (e): void => {
        e.preventDefault();
        setIsFormReady(false);
        getPreviousQuestion(applicantToken)
            .then((res) => {
                checkBackQuestion(res);
            })
            .catch(() => {
                useErrorLogger();
                setIsInErrorState(true);
            })
            .finally(() => setIsFormReady(true));
    };

    const handleSkip = (): void => {
        setIsFormReady(false);
        skipCurrentQuestion(applicantToken)
            .then((res) => {
                const applicants = selectors.getApplicantList();
                const currentApplicant = applicants.find((i) => i.applicantToken === applicantToken);
                const newApplicantList = applicants.map((a) =>
                    a === currentApplicant ?
                        {
                            ...a,
                            hasAcknowledgedSkipDialog: true
                        }
                        : a
                );
                actions.setApplicantList(newApplicantList);
                handleNextPage(res);
            })
            .catch(() => {
                useErrorLogger();
                setIsInErrorState(true);
            })
            .finally(() => setIsFormReady(true));
    };

    const handleNextPage = (res: IAFQuestionData): void => {
        if (res?.halt) {
            setIsHalted(true);
            setHaltedReasons(res?.haltReasons);
            return;
        }

        if (res?.complete) {
            setIsFormReady(false);
            const applicants = selectors.getApplicantList();
            const currentApplicant = applicants.find((i) => i.applicantToken === applicantToken);
            const nextApplicant = applicants.find((i) => i.clientNumber === currentApplicant.clientNumber + 1);
            if (!nextApplicant) {
                const pageInfo: IPageInfo = {
                    pageType: PageType.Declarations,
                    currentClientNumber: 1
                }
                setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo
                )
                history.push(`/app-form/details/${applicantToken}/declarations`);
                return;
            }
            const pageInfo: IPageInfo = {
                pageType: PageType.AppForm,
                currentClientNumber: nextApplicant.clientNumber
            }
            setCurrentPageProgress(selectors.getApplicationId(), selectors.getQuoteGroupId(), pageInfo
            )
            history.push(`/app-form/details/${nextApplicant.applicantToken}`);
        }

        if (res?.isSuccessful) {
            handleChangeQuestion(res.questions);
            actions.setQuestionResponse(res.response);
        } else {
            setIsFormReady(true);
        }
    };

    const checkBackQuestion = (info): void => {
        if (!info?.questions?.length) return;
        const questionInfo = isMultipleQuestion
            ? currentQuestionList[0]
            : currentQuestion;
        if (getQuestionID(info?.questions[0]) === getQuestionID(questionInfo)) { //if this question is the first question
            const applicantList = selectors.getApplicantList();
            const currentApplicant = applicantList.find((i) => i.applicantToken === applicantToken);
            const previousApplicant = applicantList.find((i) => i.clientNumber === currentApplicant?.clientNumber - 1);

            if (!previousApplicant) { //this is the first applicant
                const lastApplicant = applicantList.reduce((prev, current) => (prev && prev.clientNumber > current.clientNumber) ? prev : current);
                if (lastApplicant.isChild) {
                    history.push(`/app-form/setup/dateofbirth/applicant${lastApplicant.clientNumber}`);
                } else {
                    history.push(`/app-form/setup/contact/applicant${lastApplicant.clientNumber}`);
                }
            } else { //Multiple applicants, previous applicant has finished the app.
                history.push(`/app-form/details/${previousApplicant.applicantToken}`);
            }
            return;
        }
        handleChangeQuestion(info.questions);
        actions.setQuestionResponse(info.response);
    };

    const checkApplicantToken = (token) => {
        const applicants = selectors.getApplicantList();
        const applicant = applicants.find((_i) => _i.applicantToken === token);
        if (!applicant || !applicant?.applicationId) {
            redirectTo404Page();
            return;
        }
    };

    const getApplicantName = (): string => {
        return selectors.getApplicantName(applicantToken)
            ? `${selectors.getApplicantName(applicantToken)}:`
            : "";
    };

    useEffect(() => {
        if (!applicantToken) return;
        checkApplicantToken(applicantToken);
        handleGetQuestion(applicantToken);
    }, [applicantToken]);

    useEffect(() => {
        if (typeof Sentry !== 'undefined' && isInErrorState) {
            Sentry.captureMessage(`Error occured for ApplicantToken ${applicantToken} on the Insurer questions page.`, "error");
        }
    }, [isInErrorState]);

    useEffect(() => {
        const keyDownHandler = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                handleBack(event);
            }
        };

        document.addEventListener("keydown", keyDownHandler);
        return () => {
            document.removeEventListener("keydown", keyDownHandler);
        };
    }, []);

    return (
        <AFLayout isInErrorState={isInErrorState} currentPage={PageType.AppForm} applicantParam={params.applicantToken}>
            <div className={cx(styles["advice-progress"], styles["layout"])}>
                {isHalted ? (
                    <AFCannotContinue
                        reason={haltedReasons}
                    />
                ) : (
                    <>
                        {(!isFormReady || isSubmitting) && <AFQuestionPlaceholder />}
                        {isFormReady &&
                            !isSubmitting &&
                            !isMultipleQuestion &&
                            !noAvailableQuestion && (
                                <AppQuestionForm
                                    applicantName={getApplicantName()}
                                    question={currentQuestion}
                                    nextQuestion={handleNextQuestion}
                                    handleBack={handleBack}
                                    skipIsVisible={true}
                                    handleSkip={handleSkip}
                                    showSkipDialog={!selectors.getApplicantList().find((i) => i.applicantToken === applicantToken).hasAcknowledgedSkipDialog}
                                />
                            )}

                        {isFormReady &&
                            !isSubmitting &&
                            isMultipleQuestion &&
                            currentQuestionList?.length &&
                            !noAvailableQuestion && (
                                <AFMultiForms
                                    applicantName={getApplicantName()}
                                    list={currentQuestionList}
                                    nextQuestion={handleNextQuestion}
                                    formLabel={formQuestionLabel}
                                    helpText={formQuestionHelpText}
                                    formQuestionType={formQuestionType}
                                    handleBack={handleBack}
                                    skipIsVisible={true}
                                    handleSkip={handleSkip}
                                    showSkipDialog={!selectors.getApplicantList().find((i) => i.applicantToken === applicantToken).hasAcknowledgedSkipDialog}
                                />
                            )}
                    </>
                )}
            </div>
        </AFLayout>
    );
};

export default AppForm;
